import type { ColumnDef } from '@tanstack/react-table'
import { Plus, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Checkbox } from 'shared/components/ui/checkbox'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from 'shared/components/ui/dialog'
import { Label } from 'shared/components/ui/label'
import { useDataTable } from 'shared/hooks/use-data-table'
import { cn } from 'shared/lib/utils'
import { useTranslate } from 'shared/lib/i18n'
import { actsAsAdmin } from 'shared/lib/roles'
import { useAuthStore } from 'shared/store/auth'
import { PointDayMark } from 'shared/components/point-day'
import { usePointDayWords } from 'shared/components/point-day-words'

/**
 * Trips at the hub (spec §13, §26; ADR-0024; tickets 27, 33). A hub worker makes a
 * Trip — one driver and the Points to visit, in order — at their own hub, in the
 * «Новый рейс» dialog; the admin also picks the hub. The Trips are a table. Once the driver is back the Trip is closed in two steps: the
 * screen first shows the Bags still with the driver (from the Trip's checklist) —
 * each will be a Discrepancy "never reached its Point" against the Trip, pointing
 * at the driver — and only the confirmation closes it. The calls are injected
 * (`loadOptions`, `listTrips`, `createTrip`, `loadTrip`, `closeTrip`) so the
 * behaviour is tested without a backend; they default to the real tRPC client.
 */

export type TripOptions = RouterOutputs['trips']['Options']
export type TripRow = RouterOutputs['trips']['List'][number]
export type TripCreateRequest = RouterInputs['trips']['Create']
export type TripCreated = RouterOutputs['trips']['Create']
export type TripClosed = RouterOutputs['trips']['Close']
export type TripChecklist = RouterOutputs['trips']['Trip']

const optionsViaTrpc = () => trpcClient.trips.Options.query()
const listViaTrpc = (includeClosed: boolean) => trpcClient.trips.List.query({ includeClosed })
const createViaTrpc = (input: TripCreateRequest) => trpcClient.trips.Create.mutate(input)
const closeViaTrpc = (tripId: string) => trpcClient.trips.Close.mutate({ tripId })
const tripViaTrpc = (tripId: string) => trpcClient.trips.Trip.query({ tripId })

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

const SELECT_CLASS =
	'h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'

export interface HubTripsProps {
	loadOptions?: () => Promise<TripOptions>
	listTrips?: (includeClosed: boolean) => Promise<TripRow[]>
	createTrip?: (input: TripCreateRequest) => Promise<TripCreated>
	/** One Trip with its checklist — what the close shows before it is confirmed. */
	loadTrip?: (tripId: string) => Promise<TripChecklist>
	closeTrip?: (tripId: string) => Promise<TripClosed>
	/** Whether the form asks for the hub — the admin's; by default from the signed-in role. */
	pickHub?: boolean
}

export function HubTrips({
	loadOptions = optionsViaTrpc,
	listTrips = listViaTrpc,
	createTrip = createViaTrpc,
	loadTrip = tripViaTrpc,
	closeTrip = closeViaTrpc,
	pickHub,
}: HubTripsProps) {
	const { t } = useTranslate()
	const dayWords = usePointDayWords()
	const role = useAuthStore((s) => s.user?.role)
	const asksHub = pickHub ?? actsAsAdmin(role)
	const [hubId, setHubId] = useState('')
	const [options, setOptions] = useState<TripOptions | null>(null)
	const [optionsError, setOptionsError] = useState<string | null>(null)
	const [trips, setTrips] = useState<TripRow[] | null>(null)
	const [listError, setListError] = useState<string | null>(null)
	const [includeClosed, setIncludeClosed] = useState(false)
	const [driverId, setDriverId] = useState('')
	const [pointId, setPointId] = useState('')
	const [route, setRoute] = useState<string[]>([])
	const [busy, setBusy] = useState(false)
	const [createError, setCreateError] = useState<string | null>(null)
	const [created, setCreated] = useState<TripCreated | null>(null)
	const [closeError, setCloseError] = useState<string | null>(null)
	const [closed, setClosed] = useState<TripClosed | null>(null)
	const [closing, setClosing] = useState<TripChecklist | null>(null)
	const [creating, setCreating] = useState(false)
	const reads = useRef(0)

	useEffect(() => {
		loadOptions().then(
			(next) => {
				setOptions(next)
				// One hub to choose from is chosen already.
				if (next.hubs.length === 1) setHubId(next.hubs[0]!.hubId)
			},
			(error) => setOptionsError(failure(error)),
		)
	}, [loadOptions])

	// The list is the server's: re-read after every act rather than rebuilt here.
	const refresh = useCallback(() => {
		const read = ++reads.current
		listTrips(includeClosed).then(
			(next) => {
				if (read !== reads.current) return
				setTrips(next)
				setListError(null)
			},
			(error) => {
				if (read === reads.current) setListError(failure(error))
			},
		)
	}, [listTrips, includeClosed])

	useEffect(() => {
		refresh()
	}, [refresh])

	const pointCode = (id: string) => options?.points.find((p) => p.pointId === id)?.id ?? ''
	const pointOf = (id: string) => options?.points.find((p) => p.pointId === id)
	// A Point's day beside its id in the choice (ticket 45): closed, or its hours today.
	const optionLabel = (p: TripOptions['points'][number]) => {
		const words = dayWords(p)
		return [`${p.id} · ${p.region}`, words.closure, words.today].filter(Boolean).join(' · ')
	}
	const freePoints = (options?.points ?? []).filter((p) => !route.includes(p.pointId))

	const addPoint = () => {
		if (!pointId) return
		setRoute((r) => [...r, pointId])
		setPointId('')
	}

	const ready = !!driverId && route.length > 0 && (!asksHub || !!hubId)

	const onCreate = async () => {
		if (busy || !ready) return
		setBusy(true)
		setCreateError(null)
		try {
			setCreated(
				await createTrip({ ...(asksHub ? { hubId } : {}), driverId, pointIds: route }),
			)
			setRoute([])
			setDriverId('')
			setCreating(false)
			refresh()
		} catch (error) {
			setCreateError(failure(error))
		} finally {
			setBusy(false)
		}
	}

	// The first step: read the Trip's checklist to show what is still with the driver.
	const onAskClose = async (tripId: string) => {
		if (busy) return
		setBusy(true)
		setCloseError(null)
		setClosed(null)
		setCreated(null)
		try {
			setClosing(await loadTrip(tripId))
		} catch (error) {
			setCloseError(failure(error))
		} finally {
			setBusy(false)
		}
	}

	const onConfirmClose = async () => {
		if (busy || !closing) return
		setBusy(true)
		setCloseError(null)
		try {
			setClosed(await closeTrip(closing.tripId))
			setClosing(null)
			refresh()
		} catch (error) {
			setCloseError(failure(error))
		} finally {
			setBusy(false)
		}
	}

	// Rebuilt every render: the close button reads `busy` and `closing`.
	const columns: ColumnDef<TripRow>[] = [
			{
				id: 'code',
				header: t('hubTrips.colTrip'),
				cell: ({ row }) => <span className='font-mono font-semibold'>{row.original.code}</span>,
			},
			{ id: 'driver', header: t('hubTrips.driver'), cell: ({ row }) => row.original.driverName },
			{
				id: 'route',
				header: t('hubTrips.colRoute'),
				cell: ({ row: { original: trip } }) => (
					<div className='flex flex-wrap items-center gap-2'>
						<span>{trip.stops.map((s) => s.pointCode).join(' → ')}</span>
						{trip.stops
							.filter((s) => s.closure)
							.map((s) => (
								<PointDayMark key={s.pointId} day={{ ...s, today: null }} testId={`trip-stop-closed-${s.pointId}`} />
							))}
					</div>
				),
			},
			...(['waiting', 'loaded', 'delivered'] as const).map(
				(key): ColumnDef<TripRow> => ({
					id: key,
					header: t(`hubTrips.col_${key}`),
					meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
					cell: ({ row }) => (
						<span className={cn(row.original.counts[key] === 0 && 'text-muted-foreground')}>
							{row.original.counts[key]}
						</span>
					),
				}),
			),
			{
				id: 'status',
				header: t('hub.colState'),
				cell: ({ row }) => (
					<Badge variant={row.original.status === 'open' ? 'default' : 'secondary'}>
						{t(`tripStatus.${row.original.status}`)}
					</Badge>
				),
			},
			{
				id: 'actions',
				header: '',
				meta: { cellClassName: 'w-px text-right' },
				cell: ({ row: { original: trip } }) =>
					trip.status === 'open' && (
						<Button
							size='sm'
							// Closing while Bags are still with the driver records a Discrepancy for each.
							variant={trip.counts.loaded > 0 ? 'destructive' : 'outline'}
							data-testid={`trip-close-${trip.code}`}
							disabled={busy || closing !== null}
							onClick={() => onAskClose(trip.tripId)}
						>
							{t('hubTrips.close')}
						</Button>
					),
			},
	]
	const rows = trips ?? []
	const { table } = useDataTable({
		data: rows,
		columns,
		rowCount: rows.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (trip) => trip.tripId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<div className='flex flex-col gap-4' data-testid='hub-trips'>
			<div className='flex flex-wrap items-center justify-between gap-3'>
				<div className='flex items-center gap-2'>
					<Checkbox
						id='trip-show-closed'
						data-testid='trip-show-closed'
						checked={includeClosed}
						onCheckedChange={(checked) => setIncludeClosed(checked === true)}
					/>
					<Label htmlFor='trip-show-closed' className='font-normal'>
						{t('hubTrips.showClosed')}
					</Label>
				</div>
				<Dialog open={creating} onOpenChange={setCreating}>
					<DialogTrigger asChild>
						<Button data-testid='trip-new' onClick={() => setCreated(null)}>
							<Plus />
							{t('hubTrips.title')}
						</Button>
					</DialogTrigger>
					<DialogContent className='sm:max-w-lg' aria-describedby={undefined} data-testid='trip-new-dialog'>
						<DialogHeader>
							<DialogTitle>{t('hubTrips.title')}</DialogTitle>
						</DialogHeader>
						<div className='flex flex-col gap-4'>
							{optionsError && (
								<p role='alert' className='text-sm text-destructive'>
									{t('hubTrips.loadFailed', { message: optionsError })}
								</p>
							)}
							{asksHub && (
								<div className='flex flex-col gap-1.5'>
									<Label htmlFor='trip-hub'>{t('hubTrips.hub')}</Label>
									<select
										id='trip-hub'
										data-testid='trip-hub'
										className={SELECT_CLASS}
										value={hubId}
										onChange={(e) => setHubId(e.target.value)}
									>
										<option value=''>{t('hubTrips.hubPlaceholder')}</option>
										{options?.hubs.map((h) => (
											<option key={h.hubId} value={h.hubId}>
												{h.id}
											</option>
										))}
									</select>
								</div>
							)}
							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='trip-driver'>{t('hubTrips.driver')}</Label>
								<select
									id='trip-driver'
									data-testid='trip-driver'
									className={SELECT_CLASS}
									value={driverId}
									onChange={(e) => setDriverId(e.target.value)}
								>
									<option value=''>{t('hubTrips.driverPlaceholder')}</option>
									{options?.drivers.map((d) => (
										<option key={d.driverId} value={d.driverId}>
											{d.name}
										</option>
									))}
								</select>
							</div>
							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='trip-point'>{t('hubTrips.route')}</Label>
								{route.length > 0 && (
									<ol className='flex flex-col divide-y rounded-md border' data-testid='trip-route'>
										{route.map((id, i) => (
											<li
												key={id}
												className='flex items-center gap-3 px-3 py-1.5'
												data-testid={`trip-route-${id}`}
											>
												<span className='w-4 text-sm text-muted-foreground tabular-nums'>{i + 1}</span>
												<span className='font-medium'>{pointCode(id)}</span>
												{pointOf(id) && <PointDayMark day={pointOf(id)!} testId={`trip-route-day-${id}`} />}
												<Button
													className='ml-auto'
													size='icon-sm'
													variant='ghost'
													aria-label={t('hubTrips.removePoint')}
													title={t('hubTrips.removePoint')}
													onClick={() => setRoute((r) => r.filter((x) => x !== id))}
												>
													<X />
												</Button>
											</li>
										))}
									</ol>
								)}
								<div className='flex gap-2'>
									<select
										id='trip-point'
										data-testid='trip-point'
										className={SELECT_CLASS}
										value={pointId}
										onChange={(e) => setPointId(e.target.value)}
									>
										<option value=''>{t('hubTrips.pointPlaceholder')}</option>
										{freePoints.map((p) => (
											<option key={p.pointId} value={p.pointId}>
												{optionLabel(p)}
											</option>
										))}
									</select>
									<Button
										type='button'
										variant='outline'
										className='h-9'
										data-testid='trip-add-point'
										disabled={!pointId}
										onClick={addPoint}
									>
										{t('hubTrips.addPoint')}
									</Button>
								</div>
							</div>
							{createError && (
								<p role='alert' className='text-sm text-destructive' data-testid='trip-create-error'>
									{t('hubTrips.createFailed', { message: createError })}
								</p>
							)}
						</div>
						<DialogFooter>
							<Button variant='outline' onClick={() => setCreating(false)}>
								{t('hubTrips.closeCancel')}
							</Button>
							<Button data-testid='trip-create' disabled={busy || !ready} onClick={onCreate}>
								{t('hubTrips.create')}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{created && (
				<p
					role='status'
					className='rounded-md border border-emerald-500/60 bg-emerald-500/10 px-4 py-2.5 font-semibold text-emerald-800 dark:text-emerald-300'
					data-testid='trip-created'
				>
					{t('hubTrips.created', { code: created.code })}
				</p>
			)}
			{closed && <ClosedBanner trip={closed} />}
			{closeError && (
				<p role='alert' className='text-sm text-destructive' data-testid='trip-close-error'>
					{t('hubTrips.closeFailed', { message: closeError })}
				</p>
			)}

			{listError ? (
				<p className='text-sm text-destructive' data-testid='trip-list-error'>
					{t('hubTrips.loadFailed', { message: listError })}
				</p>
			) : trips === null ? (
				<p className='text-sm text-muted-foreground'>{t('hubTrips.loading')}</p>
			) : (
				<DataTable
					table={table}
					withPagination={false}
					emptyText={<span data-testid='trip-empty'>{t('hubTrips.empty')}</span>}
					row={{ attributes: (trip) => ({ 'data-testid': `trip-${trip.code}`, 'data-status': trip.status }) }}
				/>
			)}

			<AlertDialog open={closing !== null} onOpenChange={(open) => !open && !busy && setClosing(null)}>
				{closing && <CloseConfirm trip={closing} busy={busy} onConfirm={onConfirmClose} />}
			</AlertDialog>
		</div>
	)
}

/**
 * Before the close: the Bags and whole Boxes of the Trip still with the driver — each
 * becomes a Discrepancy against the Trip — or the word that every one was delivered.
 */
function CloseConfirm({
	trip,
	busy,
	onConfirm,
}: {
	trip: TripChecklist
	busy: boolean
	onConfirm: () => void
}) {
	const { t } = useTranslate()
	const left = trip.stops.flatMap((s) =>
		s.containers.filter((b) => b.stage === 'loaded').map((b) => ({ ...b, pointCode: s.pointCode })),
	)
	return (
		<AlertDialogContent
			className='data-[size=default]:sm:max-w-md'
			data-testid='trip-close-confirm'
			data-tone={left.length > 0 ? 'attention' : 'ready'}
		>
			<AlertDialogHeader>
				<AlertDialogTitle>{t('hubTrips.closeAsk', { code: trip.code })}</AlertDialogTitle>
				<AlertDialogDescription>
					{left.length > 0 ? t('hubTrips.closeLeftTitle') : t('hubTrips.closeAllDelivered')}
				</AlertDialogDescription>
			</AlertDialogHeader>
			{left.length > 0 && (
				<ul
					className='flex flex-col gap-0.5 rounded-md border border-amber-500/60 bg-amber-500/10 px-3 py-2 font-mono text-sm text-amber-800 dark:text-amber-300'
					data-testid='trip-close-left'
				>
					{left.map((b) => (
						<li key={b.containerId}>
							{t('hubTrips.stillBag', { kind: t(`ridingKind.${b.kind}`), code: b.code, point: b.pointCode })}
						</li>
					))}
				</ul>
			)}
			<AlertDialogFooter>
				<AlertDialogCancel disabled={busy}>{t('hubTrips.closeCancel')}</AlertDialogCancel>
				<Button
					variant={left.length > 0 ? 'destructive' : 'default'}
					data-testid='trip-close-confirm-button'
					disabled={busy}
					onClick={onConfirm}
				>
					{t('hubTrips.closeConfirm')}
				</Button>
			</AlertDialogFooter>
		</AlertDialogContent>
	)
}

/** The Trip just closed, and the Bags and whole Boxes that never reached their Point — each a Discrepancy now. */
function ClosedBanner({ trip }: { trip: TripClosed }) {
	const { t } = useTranslate()
	const left = trip.stillWithDriver.length > 0
	return (
		<div
			role='status'
			className={
				left
					? 'flex flex-col gap-1 rounded-md border border-amber-500/60 bg-amber-500/10 px-4 py-2.5 text-amber-800 dark:text-amber-300'
					: 'flex flex-col gap-1 rounded-md border border-emerald-500/60 bg-emerald-500/10 px-4 py-2.5 text-emerald-800 dark:text-emerald-300'
			}
			data-testid='trip-closed'
			data-tone={left ? 'attention' : 'ready'}
		>
			<div className='font-semibold'>{t('hubTrips.closedTitle', { code: trip.code })}</div>
			{left ? (
				<>
					<div className='text-sm'>{t('hubTrips.stillRecorded')}</div>
					<ul data-testid='trip-still'>
						{trip.stillWithDriver.map((b) => (
							<li key={b.containerId} className='font-mono text-sm'>
								{t('hubTrips.stillBag', { kind: t(`ridingKind.${b.kind}`), code: b.code, point: b.pointCode })}
							</li>
						))}
					</ul>
				</>
			) : (
				<div className='text-sm'>{t('hubTrips.stillNone')}</div>
			)}
		</div>
	)
}
